import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  AlertTriangle,
  ShieldCheck,
  ShieldAlert,
  Loader2,
} from 'lucide-react';
import type { ProjectDataTableItem } from '@kirin/api-client/contracts/projects';
import { TruncatedText } from '@/components/shared/TruncatedText';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { cn } from '@/lib/utils';
import { PolicyCoverageBar } from './coverageDisplay';

interface CellProps {
  row: ProjectDataTableItem;
  organizationId: string;
}

/** Project name → link to the single-project page. */
export function ProjectNameCell({
  row,
  organizationId,
}: CellProps): ReactElement {
  return (
    <Link
      to={`/${organizationId}/inventory/projects/${encodeURIComponent(row.slug)}`}
      className="font-medium text-primary hover:underline"
    >
      {row.name}
    </Link>
  );
}

/** Git origin URL, or an em-dash for repo-less folder projects. */
export function RepoUrlCell({
  row,
}: {
  row: ProjectDataTableItem;
}): ReactElement {
  return (
    <TruncatedText className="text-muted-foreground">
      {row.repoOriginUrl ?? '—'}
    </TruncatedText>
  );
}

/**
 * User count → opens a modal listing the project's users (mirrors the MCP /
 * Instructions tabs). Plain text when there are no users or no click handler.
 */
export function UsersCountCell({
  row,
  onSelectProject,
}: {
  row: ProjectDataTableItem;
  onSelectProject?: (project: ProjectDataTableItem) => void;
}): ReactElement {
  const count = (
    <span className="inline-flex items-center gap-1.5 font-medium text-muted-foreground">
      <Users className="h-3.5 w-3.5" />
      {row.userCount}
    </span>
  );

  if (!onSelectProject || row.userCount === 0) {
    return count;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={() => onSelectProject(row)}
          className="inline-flex items-center gap-1.5 font-medium text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-primary transition-colors cursor-pointer"
        >
          <Users className="h-3.5 w-3.5" />
          {row.userCount}
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Users className="h-3 w-3" />
            <span>
              {row.userCount} user{row.userCount !== 1 ? 's' : ''} on this
              project
            </span>
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Click to see user list
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * AI instruction count → link to the AI Instructions tab pre-filtered to this
 * project (the AI-instructions side reads the `project` query param).
 */
export function AiInstructionsCell({
  row,
  organizationId,
}: CellProps): ReactElement {
  const label = `${row.aiInstructionsCount} instruction file(s)`;
  if (row.aiInstructionsCount === 0) {
    return <span className="text-muted-foreground">{label}</span>;
  }
  return (
    <Link
      to={`/${organizationId}/inventory?tab=ai-instructions&org-ai-ins.ip[includes]=${encodeURIComponent(row.slug)}`}
      className="text-primary hover:underline"
    >
      {label}
    </Link>
  );
}

/**
 * Hook count → link to the Hooks tab pre-filtered to this project (the Hooks
 * Projects column filter is entity-based, keyed by project slug). Zero stays
 * plain text — nothing to link to.
 */
export function HooksCell({ row, organizationId }: CellProps): ReactElement {
  const label = `${row.hooksCount} hook(s)`;
  if (row.hooksCount === 0) {
    return <span className="text-muted-foreground">{label}</span>;
  }
  return (
    <Link
      to={`/${organizationId}/inventory?tab=hooks&org-hooks.hpr[includes]=${encodeURIComponent(row.slug)}`}
      className="text-primary hover:underline"
    >
      {label}
    </Link>
  );
}

/**
 * Security-AI-instruction coverage across all audited catalogs: a stacked
 * covered/partial/missing bar plus an "N gaps" / "All covered" label. Drives the
 * tab's default sort (missing + partial, high → low).
 */
export function PolicyCoverageCell({
  row,
}: {
  row: ProjectDataTableItem;
}): ReactElement {
  return <PolicyCoverageBar coverage={row.coverage} />;
}

/**
 * "Packages added by AI" risk badge: among packages a coding agent added to the
 * project in the last 7 days, malicious take precedence (most severe), then
 * vulnerable, else "Clean". Counts come from the project's command-event OSV
 * summaries. (Named SupplyChainCell for the internal `supplyChain` field; the
 * column header is "Packages added by AI" — KIR-3356.)
 */
export function SupplyChainCell({
  row,
}: {
  row: ProjectDataTableItem;
}): ReactElement {
  const { malicious, vulnerable, unknown } = row.supplyChain;
  if (malicious > 0) {
    return (
      <Badge
        variant="outline"
        className={cn('gap-1 border-red-500/40 text-red-600 dark:text-red-400')}
      >
        <AlertTriangle className="h-3 w-3" />
        {malicious} malicious
      </Badge>
    );
  }
  if (vulnerable > 0) {
    return (
      <Badge
        variant="outline"
        className="gap-1 border-amber-500/40 text-amber-600 dark:text-amber-400"
      >
        <ShieldAlert className="h-3 w-3" />
        {vulnerable} vulnerable
      </Badge>
    );
  }
  // No conclusive OSV verdict yet — not a settled "Clean".
  if (unknown > 0) {
    return (
      <Badge
        variant="outline"
        className="gap-1 border-muted-foreground/30 text-muted-foreground"
      >
        <Loader2 className="h-3 w-3" />
        Checking…
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="gap-1 border-green-500/40 text-green-600 dark:text-green-400"
    >
      <ShieldCheck className="h-3 w-3" />
      Clean
    </Badge>
  );
}

/**
 * Most recent security-coverage scan (`lastScannedAt`), or "Never" when the
 * coverage scanner hasn't run — distinct from `lastSeenAt` (last ingest), which
 * a coverage scan does not update.
 */
export function LastScannedCell({
  row,
}: {
  row: ProjectDataTableItem;
}): ReactElement {
  if (!row.lastScannedAt) {
    return <span className="text-muted-foreground">Never</span>;
  }
  return (
    <span className="text-muted-foreground">
      <RelativeTimeWithTooltip date={row.lastScannedAt} />
    </span>
  );
}
